import type { FormRulesStructure } from '../model/form-rules.types';
import type { FormStructure, TableElement } from '../model/form.types';
import type { FieldValue, MatrixAnswers, TableAnswers } from '../model/types';
import {
  elementLabelsById,
  repeatedElementLabels,
  hasStoredValue,
  isTableElement,
  migratedElements,
  sameAnswer,
  snapshotFields,
  walkElements,
} from './field-walk';
import { modelRowFieldByColumn } from './model-row';
import type { FieldAccessResolver } from './permissions';
import { evaluateRules, ruleCycleIds } from './rules';

type ExtraRow = Record<number, FieldValue>;

function sameIds(left: ReadonlySet<string>, right: ReadonlySet<string>): boolean {
  return left.size === right.size && [...left].every((id) => right.has(id));
}

// What a rule hides or disables keeps the saved value. Giving a value back can flip a rule that
// value triggers, either way, so the locked set is recomputed over the resulting values until it
// stops changing. Without a rule cycle the set settles within one round per element: a field's lock
// depends only on the locks of its triggers, upstream in the rule graph, whatever their place on the
// form. Every fill path refuses a matrix whose rules form a cycle (fillableMatrixProblem), so the
// bound only stops a structure that slipped past it.
export function ruleLockedIds(
  structure: FormStructure,
  rules: FormRulesStructure | null,
  base: MatrixAnswers,
  valuesFor: (locked: ReadonlySet<string>) => Record<string, FieldValue>,
): ReadonlySet<string> {
  const elements = migratedElements(structure);
  const ids = [...walkElements(elements)].map(({ element }) => element.id);
  let locked: ReadonlySet<string> = new Set();

  for (let round = 0; round <= ids.length; round++) {
    const outcome = evaluateRules(rules, snapshotFields(elements, { ...base, values: valuesFor(locked) }));
    const next = new Set(ids.filter((id) => !outcome(id).visible || !outcome(id).enabled));
    if (sameIds(next, locked)) return locked;
    locked = next;
  }
  return locked;
}

export function ruleCycleLabels(structure: FormStructure, rules: FormRulesStructure | null): string[][] {
  const knownIds = new Set([...walkElements(migratedElements(structure))].map(({ element }) => element.id));
  return ruleCycleIds(rules, knownIds).map((ids) => elementLabelsById(structure, ids));
}

export interface FillableMatrixProblem {
  kind: 'repeated-ids' | 'rule-cycle';
  labelGroups: string[][];
}

// The one order both the server and the sheet refuse a matrix in, so the edit screen never lets a
// person type an edit the server would then refuse.
export function fillableMatrixProblem(
  structure: FormStructure,
  rules: FormRulesStructure | null,
): FillableMatrixProblem | null {
  const repeated = repeatedElementLabels(structure);
  if (repeated.length > 0) return { kind: 'repeated-ids', labelGroups: repeated };
  const cycles = ruleCycleLabels(structure, rules);
  return cycles.length > 0 ? { kind: 'rule-cycle', labelGroups: cycles } : null;
}

export interface TableColumns {
  editable: number[];
  writable: number[];
  locked: number[];
}

// A column a new row may write is editable for the subject and left free by the rules. With
// editable columns but none writable, a new row could only be blank: both sides drop it.
export function tableColumns(table: TableElement, access: FieldAccessResolver, locked: ReadonlySet<string>): TableColumns {
  const columns = [...modelRowFieldByColumn(table).entries()];
  const lockedColumns = columns.filter(([, column]) => locked.has(column.element.id)).map(([columnIndex]) => columnIndex);
  const editable = columns.filter(([, column]) => access(column.element.id).editable).map(([columnIndex]) => columnIndex);
  return { editable, writable: editable.filter((columnIndex) => !lockedColumns.includes(columnIndex)), locked: lockedColumns };
}

function restoredValues(
  values: Record<string, FieldValue>,
  saved: Record<string, FieldValue>,
  locked: ReadonlySet<string>,
): Record<string, FieldValue> {
  const restored = { ...values };
  for (const id of locked) {
    if (hasStoredValue(saved, id)) restored[id] = saved[id];
    else delete restored[id];
  }
  return restored;
}

function restoredRow(row: ExtraRow, savedRow: ExtraRow | undefined, lockedColumns: number[]): ExtraRow {
  let restored: ExtraRow | null = null;
  for (const columnIndex of lockedColumns) {
    const saved = savedRow?.[columnIndex];
    const differs = saved === undefined ? columnIndex in row : !sameAnswer(row[columnIndex], saved);
    if (!differs) continue;
    restored ??= { ...row };
    if (saved === undefined) delete restored[columnIndex];
    else restored[columnIndex] = saved;
  }
  return restored ?? row;
}

// A saved row gets the saved cells of its locked columns back, as the server keeps them in a
// correction; a new row loses them, or goes away when no column is left to write.
function restoredTable(
  table: TableElement,
  rows: TableAnswers,
  savedRows: ExtraRow[],
  access: FieldAccessResolver,
  locked: ReadonlySet<string>,
): TableAnswers {
  const columns = tableColumns(table, access, locked);
  const dropNewRows = columns.editable.length > 0 && columns.writable.length === 0;
  const kept = dropNewRows ? rows.extraRows.slice(0, savedRows.length) : rows.extraRows;
  const extraRows = kept.map((row, rowIndex) => restoredRow(row, savedRows[rowIndex], columns.locked));
  const changed = kept.length !== rows.extraRows.length || extraRows.some((row, rowIndex) => row !== kept[rowIndex]);
  return changed ? { extraRows } : rows;
}

function sameValues(left: Record<string, FieldValue>, right: Record<string, FieldValue>): boolean {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].every(
    (key) => hasStoredValue(left, key) === hasStoredValue(right, key) && sameAnswer(left[key], right[key]),
  );
}

export interface RuleLockedRestoreInput {
  structure: FormStructure;
  rules: FormRulesStructure | null;
  access: FieldAccessResolver;
  saved: MatrixAnswers | null;
  answers: MatrixAnswers;
}

// The sheet's side of the rule: once a rule hides or disables a field, the field shows its saved
// value again, so a rule it triggers reads what the save will keep. Returns the same object when
// nothing changes, so a reactive caller does not loop.
export function withRuleLockedValuesRestored(input: RuleLockedRestoreInput): MatrixAnswers {
  const { answers } = input;
  const savedValues = input.saved?.values ?? {};
  const locked = ruleLockedIds(input.structure, input.rules, answers, (current) =>
    restoredValues(answers.values, savedValues, current),
  );
  if (locked.size === 0) return answers;

  const values = restoredValues(answers.values, savedValues, locked);
  const tables: Record<string, TableAnswers> = { ...answers.tables };
  let tablesChanged = false;
  for (const { element } of walkElements(migratedElements(input.structure))) {
    const rows = answers.tables[element.id];
    if (!isTableElement(element) || !rows) continue;
    const savedRows = input.saved?.tables[element.id]?.extraRows ?? [];
    const restored = restoredTable(element, rows, savedRows, input.access, locked);
    if (restored === rows) continue;
    tables[element.id] = restored;
    tablesChanged = true;
  }

  if (!tablesChanged && sameValues(values, answers.values)) return answers;
  return { ...answers, values, tables };
}
